import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { AnimatePresence, motion } from 'framer-motion';
import Seo from '../../components/Seo/Seo';
import services from '../../utils/services';

const serviceCarouselBySlug = {
  'carpet-cleaning-brisbane': [
    '/service-carousel/CarpetSteam1.jpg',
    '/service-carousel/CarpetSteam2.jpg',
    '/service-carousel/CarpetSteam3.jpg',
  ],
  'couch-cleaning-brisbane': [
    '/service-carousel/CouchSteam1.jpg',
    '/service-carousel/CouchSteam2.jpg',
    '/service-carousel/CouchSteam3.jpg',
  ],
  'pest-control-brisbane': [
    '/service-carousel/PestControl1.jpg',
    '/service-carousel/PestControl2.jpg',
    '/service-carousel/PestControl3.jpg',
  ],
  'mattress-cleaning-brisbane': [
    '/service-carousel/MattressCleaning1.jpg',
    '/service-carousel/MattressCleaning2.jpg',
    '/service-carousel/MattressCleaning3.jpg',
  ],
  'tile-and-grout-cleaning-brisbane': [
    '/service-carousel/Tile1.jpg',
    '/service-carousel/Tile2.jpg',
    '/service-carousel/Tile3.jpg',
  ],
  'end-of-lease-cleaning-brisbane': [
    '/service-carousel/BondCleaning1.jpg',
    '/service-carousel/BondCleaning2.jpg',
    '/service-carousel/BondCleaning3.jpg',
  ],
  'apartment-cleaning-brisbane': [
    '/service-carousel/ApartmentCleaning1.jpg',
    '/service-carousel/ApartmentCleaning2.jpg',
    '/service-carousel/ApartmentCleaning3.jpg',
  ],
};

const beforeAfterBySlug = {
  'carpet-cleaning-brisbane': {
    before: '/BeforeAfter/CarpetBefore.png',
    after: '/BeforeAfter/CarpetAfter.png',
  },
  'couch-cleaning-brisbane': {
    before: '/BeforeAfter/CouchBefore.png',
    after: '/BeforeAfter/CouchAfter.png',
  },
  'mattress-cleaning-brisbane': {
    before: '/BeforeAfter/MattressBefore.png',
    after: '/BeforeAfter/MattressAfter.png',
  },
  'tile-and-grout-cleaning-brisbane': {
    before: '/BeforeAfter/TileBefore.png',
    after: '/BeforeAfter/TileAfter.png',
  },
  'end-of-lease-cleaning-brisbane': {
    before: '/BeforeAfter/BondBefore.png',
    after: '/BeforeAfter/BondAfter.png',
  },
};

const ServiceDetails = () => {
  const { slug } = useParams();
  const service = services.find((item) => item.slug === slug);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const IconComponent = service.icon;
  const beforeAfter = beforeAfterBySlug[service.slug];
  const carouselImages = serviceCarouselBySlug[service.slug] || (service.image ? [service.image] : []);

  const renderFeatureText = (feature) => {
    if (Array.isArray(feature)) {
      return feature.map((item, index) => (
        <React.Fragment key={`${feature}-${index}`}>{item}</React.Fragment>
      ));
    }

    if (React.isValidElement(feature)) {
      return feature;
    }

    return <span>{feature}</span>;
  };

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [service.slug]);

  useEffect(() => {
    if (carouselImages.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
    }, 3500);

    return () => window.clearInterval(intervalId);
  }, [carouselImages]);

  return (
    <div>
      <Seo
        title={service.name}
        description={service.shortDescription}
        canonicalPath={`/services/${service.slug}`}
      />
      <div className="page-header">
        <div className="container-custom">
          <h1>{service.name}</h1>
          <p>{service.shortDescription}</p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              {carouselImages.length > 0 ? (
                <div className="w-full max-w-[520px] rounded-2xl border border-gray-100 overflow-hidden bg-white">
                  <div className="relative h-[220px] sm:h-[260px] lg:h-[320px] bg-white">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={carouselImages[currentImageIndex]}
                        src={carouselImages[currentImageIndex]}
                        alt={`${service.name} preview ${currentImageIndex + 1}`}
                        className="absolute inset-0 h-full w-full object-contain"
                        initial={{ opacity: 0.3 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0.3 }}
                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                      />
                    </AnimatePresence>
                  </div>

                  {carouselImages.length > 1 ? (
                    <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100">
                      {carouselImages.map((image, index) => (
                        <button
                          key={image}
                          type="button"
                          onClick={() => setCurrentImageIndex(index)}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            index === currentImageIndex ? 'w-6 bg-primary-500' : 'w-2 bg-gray-300'
                          }`}
                          aria-label={`Show ${service.name} preview image ${index + 1}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-100 bg-gray-50 min-h-[260px] flex items-center justify-center">
                  <div className="text-center">
                    <IconComponent className="mx-auto text-5xl text-primary-500 mb-3" />
                    <p className="text-gray-500 text-sm">Image will be added soon</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                <IconComponent className="text-2xl text-primary-500" />
              </div>

              <h2 className="text-3xl font-heading font-bold text-gray-800 mb-4">Service Overview</h2>
              <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/booking" className="btn-primary inline-flex items-center justify-center gap-2">
                  Book This Service
                  <FaArrowRight className="text-sm" />
                </Link>
                <Link to="/services" className="btn-outline inline-flex items-center justify-center gap-2">
                  Back to Services
                </Link>
              </div>
            </div>
          </div>

          {beforeAfter ? (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
              <div>
                <h3 className="text-xl font-heading font-semibold text-gray-800 mb-4">Before & After</h3>
                {beforeAfter.before && beforeAfter.after ? (
                  <div className="rounded-xl overflow-hidden border border-gray-100 bg-white">
                    <div className="h-40 sm:h-48 lg:h-56 relative overflow-hidden flex-none">
                      <div className="grid h-full grid-cols-2">
                        <div className="relative">
                          <img
                            src={beforeAfter.before}
                            alt={`${service.name} before`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                          <span className="absolute left-2 top-2 rounded bg-black/65 px-2 py-1 text-[10px] font-semibold tracking-wide text-white">
                            BEFORE
                          </span>
                        </div>
                        <div className="relative">
                          <img
                            src={beforeAfter.after}
                            alt={`${service.name} after`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                          <span className="absolute right-2 top-2 rounded bg-success px-2 py-1 text-[10px] font-semibold tracking-wide text-white">
                            AFTER
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 border-t border-gray-100 bg-white relative z-10">
                      <h4 className="font-heading font-semibold text-gray-800 text-sm mb-1 leading-snug">
                        {service.name} Result
                      </h4>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1">
                        Side-by-side before and after comparison.
                      </p>
                      <p className="text-gray-400 text-xs">Before &amp; After</p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-100 bg-gray-50 h-44 sm:h-52 flex items-center justify-center">
                    <p className="text-gray-500 text-sm font-medium">Before/after images coming soon</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xl font-heading font-semibold text-gray-800 mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {service.features.map((feature, index) => (
                    <li key={`${service.slug}-${index}`} className="flex items-start gap-2">
                      <FaCheckCircle className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{renderFeatureText(feature)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="mt-10">
              <h3 className="text-xl font-heading font-semibold text-gray-800 mb-4">What's Included</h3>
              <ul className="space-y-3">
                {service.features.map((feature, index) => (
                  <li key={`${service.slug}-${index}`} className="flex items-start gap-2">
                    <FaCheckCircle className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">{renderFeatureText(feature)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServiceDetails;
